import React, { useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { api, fmtINR, fmtINRcompact, fmtDate } from "@/lib/api";
import { Skeleton, StatusPill } from "@/components/Atoms";
import { Download } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);
const TABS = [
  { id: "schedule", label: "Schedule" },
  { id: "history",  label: "History"  },
  { id: "soa",      label: "Statement"},
];

export default function BuyerFinancials() {
  const { data } = useSWR("/me/financials", fetcher);
  const [tab, setTab] = useState("schedule");

  return (
    <div className="space-y-5">
      <div>
        <div className="rc-title-1">Financials</div>
        <div className="rc-callout">Your demand schedule, payments, and full statement of account.</div>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <Stat label="Total Value"  full={data ? fmtINR(data.summary.total_demand) : "—"} short={data ? fmtINRcompact(data.summary.total_demand) : "—"} tid="stat-total"/>
        <Stat label="Paid"         full={data ? fmtINR(data.summary.total_paid) : "—"}   short={data ? fmtINRcompact(data.summary.total_paid) : "—"}   accent="var(--rc-success)" tid="stat-paid"/>
        <Stat label="Outstanding"  full={data ? fmtINR(data.summary.outstanding) : "—"}  short={data ? fmtINRcompact(data.summary.outstanding) : "—"}  accent={data?.summary.outstanding > 0 ? "var(--rc-orange)" : "var(--rc-success)"} tid="stat-outstanding"/>
      </div>

      <div className="rc-card rc-card-padded">
        <div className="flex gap-2 mb-4 overflow-x-auto rc-noscroll">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`rc-btn rc-btn-sm ${tab === t.id ? "rc-btn-primary" : "rc-btn-secondary"}`}
              data-testid={`fin-tab-${t.id}`}>{t.label}</button>
          ))}
        </div>

        {!data && <Skeleton h={280}/>}

        {data && tab === "schedule" && (
          <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
            <table className="w-full text-left rc-tabular" style={{ minWidth: 540 }}>
              <thead>
                <tr className="rc-caption uppercase">
                  <th className="py-2 pr-4 whitespace-nowrap">Milestone</th>
                  <th className="py-2 pr-4 whitespace-nowrap">Due</th>
                  <th className="py-2 pr-4 text-right whitespace-nowrap">Amount</th>
                  <th className="py-2 pr-4 whitespace-nowrap">Status</th>
                  <th className="py-2 pr-4 whitespace-nowrap">Receipt</th>
                </tr>
              </thead>
              <tbody>
                {[...data.demands].sort((a,b) => (a.status === "overdue" ? -1 : 0)).map((d, i) => (
                  <tr key={i} className="border-t border-[var(--rc-line)]" data-testid={`demand-row-${i}`}>
                    <td className="py-3 pr-4 rc-body whitespace-nowrap">{d.milestone}</td>
                    <td className="py-3 pr-4 rc-body whitespace-nowrap">{fmtDate(d.due_date)}</td>
                    <td className="py-3 pr-4 rc-body text-right whitespace-nowrap">
                      <span className="sm:hidden">{fmtINRcompact(d.amount)}</span>
                      <span className="hidden sm:inline">{fmtINR(d.amount)}</span>
                    </td>
                    <td className="py-3 pr-4 whitespace-nowrap"><StatusPill status={d.status} /></td>
                    <td className="py-3 pr-4 rc-footnote whitespace-nowrap">{d.receipt_no || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.demands.length === 0 && <div className="rc-footnote py-8 text-center">No demand schedule yet.</div>}
          </div>
        )}

        {data && tab === "history" && (
          <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
            <table className="w-full text-left rc-tabular" style={{ minWidth: 600 }}>
              <thead><tr className="rc-caption uppercase">
                <th className="py-2 pr-4 whitespace-nowrap">Date</th><th className="py-2 pr-4 whitespace-nowrap">Mode</th>
                <th className="py-2 pr-4 whitespace-nowrap">Reference</th><th className="py-2 pr-4 text-right whitespace-nowrap">Amount</th>
                <th className="py-2 pr-4"> </th>
              </tr></thead>
              <tbody>
                {data.payments.map((p, i) => (
                  <tr key={i} className="border-t border-[var(--rc-line)]">
                    <td className="py-3 pr-4 whitespace-nowrap">{fmtDate(p.cheque_date || p.created_at)}</td>
                    <td className="py-3 pr-4 whitespace-nowrap">{p.payment_mode}</td>
                    <td className="py-3 pr-4 rc-footnote truncate max-w-[200px]">{p.cheque_no}</td>
                    <td className="py-3 pr-4 text-right whitespace-nowrap" style={{ color: "var(--rc-success)" }}>
                      <span className="sm:hidden">{fmtINRcompact(Math.abs(p.amount))}</span>
                      <span className="hidden sm:inline">{fmtINR(Math.abs(p.amount))}</span>
                    </td>
                    <td className="py-3 pr-4 whitespace-nowrap">
                      <button onClick={() => toast.success("Receipt will be emailed")} className="rc-btn rc-btn-sm rc-btn-secondary"><Download size={14}/> Receipt</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.payments.length === 0 && <div className="rc-footnote py-8 text-center">No payments recorded yet.</div>}
          </div>
        )}

        {data && tab === "soa" && (
          <SOAView rows={data.soa} />
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, full, short, accent, tid }) => (
  <div className="rc-card rc-card-padded" data-testid={tid} style={{ padding: "14px" }}>
    <div className="rc-caption uppercase tracking-wider" style={{ fontSize: 10 }}>{label}</div>
    <div className="mt-1 rc-tabular sm:hidden" style={{ color: accent || "inherit", fontSize: 17, fontWeight: 700, lineHeight: 1.1 }}>{short}</div>
    <div className="mt-1 rc-tabular hidden sm:block" style={{ color: accent || "inherit", fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>{full}</div>
  </div>
);

const SOAView = ({ rows }) => {
  const [page, setPage] = useState(0);
  const pageSize = 50;
  const total = rows.length;
  const slice = rows.slice(page * pageSize, page * pageSize + pageSize);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="rc-footnote">{total} entries</div>
        <button onClick={() => toast.success("SOA will be emailed to you")} className="rc-btn rc-btn-sm rc-btn-secondary"><Download size={14}/> Download</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left rc-tabular text-[14px]">
          <thead><tr className="rc-caption uppercase sticky top-0 bg-white">
            <th className="py-2 pr-3">Date</th>
            <th className="py-2 pr-3">Description</th>
            <th className="py-2 pr-3 text-right">Debit</th>
            <th className="py-2 pr-3 text-right">Credit</th>
            <th className="py-2 pr-3 text-right">Balance</th>
          </tr></thead>
          <tbody>
            {slice.map((r, i) => {
              const balance = (Number(r.debit_bal) || 0) - (Number(r.credit_bal) || 0);
              return (
                <tr key={i} className="border-t border-[var(--rc-line)]">
                  <td className="py-2 pr-3">{fmtDate(r.trans_date)}</td>
                  <td className="py-2 pr-3 max-w-[280px] truncate">{r.address_text?.slice(0, 60) || r.unit_no || r.subproject_name || "Entry"}</td>
                  <td className="py-2 pr-3 text-right">{r.debit_bal ? fmtINR(r.debit_bal) : ""}</td>
                  <td className="py-2 pr-3 text-right" style={{ color: "var(--rc-success)" }}>{r.credit_bal ? fmtINR(r.credit_bal) : ""}</td>
                  <td className="py-2 pr-3 text-right" style={{ color: balance > 0 ? "var(--rc-orange)" : "var(--rc-success)" }}>{fmtINR(balance)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {total === 0 && <div className="rc-footnote py-8 text-center">No SOA entries.</div>}
      </div>
      {total > pageSize && (
        <div className="flex items-center justify-between mt-3">
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="rc-btn rc-btn-sm rc-btn-secondary">Prev</button>
          <div className="rc-footnote">Page {page + 1} of {Math.ceil(total/pageSize)}</div>
          <button disabled={(page + 1) * pageSize >= total} onClick={() => setPage(p => p + 1)} className="rc-btn rc-btn-sm rc-btn-secondary">Next</button>
        </div>
      )}
    </div>
  );
};
